import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useDestinationBySlug } from '../hooks/useCMS';
import SafeImage from '../components/SafeImage';
import CountryFlag from '../components/CountryFlag';
import { ReasonItem, TransportItem } from '../types/cms';
import SEOHead from '../components/SEOHead';
import { getCanonicalUrl, SITE_CONFIG } from '../lib/seo';
import { useWhatsApp } from '../contexts/WhatsAppContext';

function DestinoDetalle() {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = useDestinationBySlug(slug || '');
  const destino = data?.destination;
  const { setMessage, clearMessage } = useWhatsApp();
  
  // Armar mensaje de WhatsApp para el destino
  const whatsappMsg = destino ? `Hola! Quiero consultar sobre viajes a ${destino.title}` : null;
  
  // Actualizar el mensaje en el contexto cuando cambie el destino
  React.useEffect(() => {
    if (destino && whatsappMsg) {
      setMessage(whatsappMsg);
    }
    // Limpiar el mensaje cuando se desmonte el componente
    return () => {
      clearMessage();
    };
  }, [destino, whatsappMsg, setMessage, clearMessage]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !destino) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          No se pudo cargar el destino. Intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }

  // Utilidad para mostrar el icono o una estrella por defecto
  const renderIcon = (icon: string | undefined) => (
    <span style={{ fontSize: 24 }}>{icon && icon.trim() !== '' ? icon : '⭐'}</span>
  );

  // Utilidad para mostrar el icono de tips o el icono de información por defecto
  const renderTipIcon = (icon: string | undefined) => (
    <span style={{ fontSize: 24 }}>{icon && icon.trim() !== '' ? icon : 'ℹ️'}</span>
  );

  // Generar datos SEO - siempre renderizar, incluso durante loading
  const seoTitle = destino
    ? `${destino.title}, ${destino.country?.name || ''} - Guía Completa`
    : undefined;
  
  const seoDescription = destino
    ? `${destino.description || ''} ${destino.destinationIntro ? destino.destinationIntro : ''} Descubrí paquetes disponibles y tips para tu viaje desde Uruguay.`
    : undefined;
  
  const seoKeywords = destino
    ? [
        destino.title,
        `viajar a ${destino.title}`,
        `turismo en ${destino.country?.name || ''}`,
        `destino ${destino.title}`,
        ...(destino.country ? [`viajes a ${destino.country.name}`] : []),
        'guía de viaje',
        'destino turístico',
      ]
    : undefined;
  
  const seoImage = destino?.imageCover?.publicUrl;
  // Usar el slug de la URL actual si no hay destino aún
  const seoUrl = destino ? `/destinos/${destino.slug}` : (slug ? `/destinos/${slug}` : '/destinos');

  // Schema JSON-LD para Place
  const placeSchema = destino ? {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: destino.title,
    description: destino.description,
    image: destino.imageCover?.publicUrl || `${SITE_CONFIG.url}${SITE_CONFIG.defaultOGImage}`,
    url: getCanonicalUrl(`/destinos/${destino.slug}`),
    ...(destino.country && {
      addressCountry: destino.country.name,
    }),
    ...(destino.packages && destino.packages.length > 0 && {
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Paquetes de Viaje',
        itemListElement: destino.packages.map((pkg, index) => ({
          '@type': 'Offer',
          position: index + 1,
          itemOffered: {
            '@type': 'Product',
            name: pkg.title,
            url: getCanonicalUrl(`/paquetes/${pkg.slug}`),
          },
        })),
      },
    }),
  } : undefined;

  return (
    <>
      <SEOHead
        key={destino?.id || slug} // Forzar re-render cuando cambian los datos
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        url={seoUrl}
        type="article"
        schema={placeSchema}
      />
      <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button component={Link} to="/destinos" variant="outlined" sx={{ mb: 2 }}>
        ← Volver a destinos
      </Button>
      <Grid container spacing={4}>
        {/* Imagen principal */}
        <Grid item xs={12} md={6}>
          <Card>
            <SafeImage
              src={destino.imageCover?.publicUrl}
              alt={destino.title}
              height={320}
              sx={{ width: '100%', objectFit: 'cover' }}
            />
          </Card>
        </Grid>
        {/* Info principal */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" gutterBottom>{destino.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CountryFlag
              src={destino.country?.bandera?.publicUrl}
              alt={destino.country?.name || 'Bandera del país'}
              width={32}
              height={20}
              sx={{ mr: 1 }}
            />
            <Typography variant="body1" color="text.secondary">
              {destino.country?.name}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>{destino.description}</Typography>
          {destino.destinationIntro && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{destino.destinationIntro}</Typography>
          )}
        </Grid>
      </Grid>

      {/* Galería de imágenes */}

      {/* Top 5 razones para visitar */}
      {destino.top5ReasonsContent && Array.isArray(destino.top5ReasonsContent) && destino.top5ReasonsContent.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>{destino.top5ReasonsTitle || 'Top 5 razones para visitar'}</Typography>
          <List>
            {(destino.top5ReasonsContent as ReasonItem[]).map((reason: ReasonItem, i: number) => (
              <ListItem key={i}>
                <ListItemIcon>
                  {renderIcon(reason.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={reason.title}
                  secondary={reason.description}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Cómo moverse */}
      {destino.howToGetAround && Array.isArray(destino.howToGetAround) && destino.howToGetAround.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Cómo moverse</Typography>
          <List>
            {(destino.howToGetAround as TransportItem[]).map((item: TransportItem, i: number) => (
              <ListItem key={i}>
                <ListItemIcon>
                  {renderIcon(item.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.description}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Tips */}
      {destino.tips && destino.tips.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Tips</Typography>
          <List>
            {destino.tips.map(tip => (
              <ListItem key={tip.id}>
                <ListItemIcon>
                  {renderTipIcon(tip.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={tip.title}
                  secondary={tip.description}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Paquetes sugeridos */}
      {destino.packages && destino.packages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Paquetes sugeridos</Typography>
          <Grid container spacing={2}>
            {destino.packages.map(pkg => (
              <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                <Card>
                  <SafeImage
                    src={pkg.imageCover?.publicUrl}
                    alt={pkg.title}
                    height={120}
                    sx={{ width: '100%', objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6">{pkg.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Desde ${pkg.priceFrom?.toLocaleString()?.replace(/,/g, '.')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      component={Link}
                      to={`/paquetes/${pkg.slug}`}
                    >
                      Ver paquete
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
    </>
  );
}

export default DestinoDetalle; 